import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, ShieldCheck } from "lucide-react";

const BASES = [
  { v: "Sales", l: "CRM (zoals Sales)" },
  { v: "Alleen lezen", l: "Alleen lezen" },
  { v: "QA", l: "QA" },
  { v: "Productie", l: "Productie" },
  { v: "Magazijn", l: "Magazijn" },
  { v: "Expeditie", l: "Expeditie" },
  { v: "TD", l: "TD (techniek)" },
  { v: "Bedrijfsleiding 2", l: "Bedrijfsleiding 2 (productie lezen + CRM)" },
];

export default function RoleManager({ onChanged }) {
  const [roles, setRoles] = useState([]);
  const [label, setLabel] = useState("");
  const [base, setBase] = useState("Sales");
  const [crm, setCrm] = useState(true);
  const [delRole, setDelRole] = useState(null);
  const [reassign, setReassign] = useState("");

  const load = () => api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const refresh = () => { load(); onChanged && onChanged(); };

  const add = async () => {
    if (!label.trim()) return toast.error("Geef de rol een naam.");
    try { await api.post("/roles", { label: label.trim(), base, crm }); toast.success("Rol toegevoegd"); setLabel(""); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async (withReassign) => {
    try {
      const q = withReassign && reassign ? `?reassign_to=${encodeURIComponent(reassign)}` : "";
      await api.delete(`/roles/${encodeURIComponent(delRole.key)}${q}`);
      toast.success("Rol verwijderd"); setDelRole(null); setReassign(""); refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.message === "in_use") setDelRole({ ...delRole, count: d.count });
      else toast.error(formatApiError(e));
    }
  };

  const custom = roles.filter((r) => !r.builtin);
  const reassignOptions = roles.filter((r) => r.key !== delRole?.key);

  return (
    <div className="mb-6 rounded-md border border-slate-200 bg-white p-4" data-testid="role-manager">
      <div className="flex items-center gap-2 mb-3"><ShieldCheck className="h-4 w-4 text-blue-700" /><h2 className="text-sm font-bold text-slate-800">Rollen beheren</h2></div>
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Naam nieuwe rol</label>
          <Input data-testid="role-new-name" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="bijv. Sales binnendienst" className="w-56" /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Sjabloon (basis-toegang)</label>
          <Select value={base} onValueChange={setBase}><SelectTrigger data-testid="role-new-base" className="w-64"><SelectValue /></SelectTrigger>
            <SelectContent>{BASES.map((b) => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}</SelectContent></Select></div>
        <label className="flex items-center gap-2 pb-2 text-sm text-slate-600"><input type="checkbox" data-testid="role-new-crm" className="h-4 w-4 accent-[#1C64B0]" checked={crm} onChange={(e) => setCrm(e.target.checked)} /> Ook toegang tot CRM/Sales</label>
        <Button data-testid="role-add-btn" onClick={add} className="bg-[#1C64B0] hover:bg-[#154E8A]"><Plus className="h-4 w-4 mr-1" /> Rol toevoegen</Button>
      </div>
      {custom.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-500 mb-1">Eigen rollen</div>
          <div className="flex flex-wrap gap-2">
            {custom.map((r) => (
              <span key={r.key} data-testid="role-chip" className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-700">
                {r.label} <span className="text-[10px] text-slate-400">({r.base}{r.crm ? " · CRM" : ""})</span>
                <button data-testid={`role-del-${r.key}`} onClick={() => { setDelRole(r); setReassign(""); }} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
        </div>
      )}
      <p className="mt-3 text-xs text-slate-400">Nieuwe rollen erven de toegang van hun sjabloon. Fijn-afstemmen van zichtbare tabbladen doe je in de tabel hieronder.</p>

      <Dialog open={!!delRole} onOpenChange={(v) => { if (!v) { setDelRole(null); setReassign(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Rol verwijderen</DialogTitle>
            <DialogDescription>{delRole?.count ? `Er zijn nog ${delRole.count} gebruiker(s) met de rol "${delRole?.label}". Kies eerst een nieuwe rol voor hen.` : `Weet je zeker dat je "${delRole?.label}" wilt verwijderen?`}</DialogDescription></DialogHeader>
          {delRole?.count ? (
            <div className="py-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nieuwe rol voor deze gebruikers</label>
              <Select value={reassign} onValueChange={setReassign}><SelectTrigger data-testid="role-reassign"><SelectValue placeholder="Kies een rol" /></SelectTrigger>
                <SelectContent>{reassignOptions.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent></Select>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDelRole(null); setReassign(""); }}>Annuleren</Button>
            <Button data-testid="role-del-confirm" className="bg-rose-600 hover:bg-rose-700" disabled={!!delRole?.count && !reassign} onClick={() => doDelete(!!delRole?.count)}>Verwijderen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
